"use client";

import { useState } from "react";

import { useCart } from "@/components/cart/cart-provider";
import { trackCatalogEvent } from "@/components/catalog/analytics";
import { isEcommercePreviewReadOnly } from "@/config/preview-mode";
import { bolivianCustomerPhoneSchema } from "@/domain/order/order-intent";

interface OrderIntentResponse {
  readonly whatsappUrl: string;
  readonly quoteWarning: string | null;
}

interface QuoteWarning {
  readonly submissionId: string;
  readonly message: string;
  readonly whatsappUrl: string;
}

export function useWhatsAppOrderSubmit(
  customerPhone: string,
  onPhoneInvalid?: () => void,
) {
  const { items, sessionId, submissionId, shippingDestination, totals } = useCart();
  const [status, setStatus] = useState<"idle" | "pending">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [quoteWarning, setQuoteWarning] = useState<QuoteWarning | null>(null);
  const parsedCustomerPhone = bolivianCustomerPhoneSchema.safeParse(customerPhone);
  const isReadOnlyPreview = isEcommercePreviewReadOnly();
  const canRenderAction =
    !isReadOnlyPreview &&
    items.length > 0 &&
    Boolean(sessionId) &&
    Boolean(submissionId) &&
    Boolean(shippingDestination);
  const isDisabled =
    isReadOnlyPreview ||
    status === "pending" ||
    items.length === 0 ||
    !sessionId ||
    !submissionId ||
    !shippingDestination;

  async function submitOrder() {
    if (isDisabled) {
      return;
    }

    if (!parsedCustomerPhone.success) {
      onPhoneInvalid?.();
      return;
    }

    setStatus("pending");
    setErrorMessage(null);
    setQuoteWarning(null);

    try {
      const response = await fetch("/api/whatsapp/order-intents", {
        body: JSON.stringify({
          customerPhone: parsedCustomerPhone.data,
          orderIntentId: submissionId,
          sessionId,
          // Only the destination key travels: the server computes the shipping
          // amount, so the client never sends one.
          shippingDestination,
          items: items.map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
          })),
          sourceUrl: window.location.href,
        }),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as
          | { readonly message?: string }
          | null;

        throw new Error(body?.message ?? "No se pudo crear el pedido.");
      }

      const result = (await response.json()) as OrderIntentResponse;
      trackCatalogEvent(
        {
          eventName: "whatsapp_order_click",
          payload: {
            itemCount: totals.itemCount,
            productCount: items.length,
            source: "cart",
          },
        },
        { keepalive: true },
      );

      if (result.quoteWarning) {
        // A `window.open(url, "_blank", "noopener,noreferrer")` call returns
        // `null` even when the tab really opened, because `noopener` makes the
        // WindowProxy unavailable per the HTML specification. It also runs
        // after an `await`, so popup blockers can reject it. Never use it to
        // detect a blocked popup: the cart page must stay mounted so the
        // warning remains readable, so the user continues through an explicit
        // anchor instead; an anchor navigation is not popup-blocked. The order
        // and its WhatsApp message already exist server-side, so waiting for
        // one human click loses nothing.
        //
        // The warning is keyed by the submission it belongs to: a later cart
        // edit rotates `submissionId`, and the anchor must never keep pointing
        // at the previous order's message.
        setQuoteWarning({
          submissionId,
          message: result.quoteWarning,
          whatsappUrl: result.whatsappUrl,
        });
        return;
      }

      window.location.assign(result.whatsappUrl);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "No se pudo enviar el pedido.");
    } finally {
      setStatus("idle");
    }
  }

  // Expose the warning only while its submission is still the cart's current
  // one. Rotating the cart reference hides it without an extra state update.
  const currentQuoteWarning =
    quoteWarning?.submissionId === submissionId ? quoteWarning : null;

  return {
    canRenderAction,
    errorMessage,
    isDisabled,
    status,
    warningMessage: currentQuoteWarning?.message ?? null,
    warningWhatsappUrl: currentQuoteWarning?.whatsappUrl ?? null,
    submitOrder,
  };
}
